import { useLayoutEffect, useState } from 'react';
import { useAdaptive } from '@/lib/responsive';
/**
 * Iframe sizing for official Meta placement previews.
 *
 * Meta renders each placement at its own fixed size — roughly 335×450 for feed,
 * 360×640 for story and reel — and serves it in an iframe we cannot restyle from
 * the outside. So the iframe is given Meta's native dimensions and CSS-scaled
 * down to the column, while its container reserves exactly the scaled height.
 * Without the reserved height a 9:16 story gets an inner scrollbar and a 1:1
 * feed leaves a band of dead space beneath it.
 *
 * Shared by the signed-in gallery and the public review page. They had diverged
 * — the public page rendered a bare iframe with no sizing at all — which is the
 * whole reason this lives in its own module rather than beside one of them.
 */

/** The column width a preview card gets on every surface that shows one. */
export const PREVIEW_COLUMN_PX = 340;

/** Fallback height when Meta reported no dimensions for a placement. */
const UNSIZED_FALLBACK_PX = 520;

export interface PreviewDimensions {
  width?: number | null;
  height?: number | null;
}

export function previewScale(variant: PreviewDimensions, availableWidth = PREVIEW_COLUMN_PX): number {
  const width = variant.width ?? 0;
  // Never scale *up*: a preview enlarged past Meta's own render is blurry and
  // misrepresents what the reviewer is approving.
  return width > availableWidth ? Math.max(1, availableWidth) / width : 1;
}

/** Height to reserve on `.meta-preview-frame`. */
export function frameBox(variant: PreviewDimensions, availableWidth = PREVIEW_COLUMN_PX) {
  if (!variant.width || !variant.height) return undefined;
  return { height: Math.round(variant.height * previewScale(variant, availableWidth)) };
}

/** Native size plus the scale transform, applied to the iframe itself. */
export function frameScale(variant: PreviewDimensions, availableWidth = PREVIEW_COLUMN_PX) {
  if (!variant.width || !variant.height) {
    // Meta did not report dimensions; fall back to filling the column.
    return { width: '100%', height: UNSIZED_FALLBACK_PX };
  }
  return {
    width: variant.width,
    height: variant.height,
    transform: `scale(${previewScale(variant, availableWidth)})`,
  };
}

/** Actual card width on adaptive layouts; desktop keeps its existing 340px cap. */
export function usePreviewWidth() {
  const adaptive = useAdaptive();
  const [element, frameRef] = useState<HTMLDivElement | null>(null);
  const [previewWidth, setWidth] = useState(PREVIEW_COLUMN_PX);
  useLayoutEffect(() => {
    if (!adaptive || !element) { setWidth(PREVIEW_COLUMN_PX); return; }
    const measure = () => setWidth(Math.max(1, Math.min(PREVIEW_COLUMN_PX, element.clientWidth)));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [adaptive, element]);
  return { frameRef, previewWidth };
}
